/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
// Units of measure a product can be priced and ordered in: metric, imperial /
// US, Indian and trade-specific units, grouped for the searchable picker.
// `one` is the stored value (Product.priceUnit) — never rename an existing
// one, older listings hold these exact strings. `uqc` is the GST Unique
// Quantity Code for e-invoices / GSTR-1 where one applies; `aka` are other
// spellings and abbreviations people search for.

const u = (one, many, uqc = '', aka = []) => ({ one, many, uqc, aka });

export const UNIT_GROUPS = [
    { group: 'Count & packaging', units: [
        u('piece', 'pieces', 'PCS', ['pc', 'pcs', 'nos', 'number', 'numbers', 'ea', 'each']),
        u('unit', 'units', 'UNT', ['u']),
        u('set', 'sets', 'SET'),
        u('pair', 'pairs', 'PRS', ['pr']),
        u('dozen', 'dozen', 'DOZ', ['doz', 'dz']),
        u('gross', 'gross', 'GRS', ['144']),
        u('great gross', 'great gross', 'GGK'),
        u('score', 'scores', 'OTH', ['20 pieces']),
        u('box', 'boxes', 'BOX', ['bx']),
        u('carton', 'cartons', 'CTN', ['ctn', 'case', 'master carton']),
        u('case', 'cases', 'OTH', ['cs']),
        u('pack', 'packs', 'PAC', ['pkt', 'packet', 'pk']),
        u('packet', 'packets', 'PAC'),
        u('bag', 'bags', 'BAG', ['sack', 'gunny bag']),
        u('sack', 'sacks', 'BAG'),
        u('bale', 'bales', 'BAL', ['bl']),
        u('bundle', 'bundles', 'BDL', ['bdl']),
        u('bunch', 'bunches', 'BUN'),
        u('bucket', 'buckets', 'BKL'),
        u('bottle', 'bottles', 'BTL', ['btl']),
        u('jar', 'jars', 'OTH'),
        u('can', 'cans', 'CAN', ['tin']),
        u('tin', 'tins', 'CAN'),
        u('drum', 'drums', 'DRM', ['barrel drum']),
        u('jerrycan', 'jerrycans', 'CAN', ['jerry can']),
        u('tube', 'tubes', 'TUB'),
        u('roll', 'rolls', 'ROL', ['rl']),
        u('reel', 'reels', 'ROL'),
        u('coil', 'coils', 'OTH'),
        u('sheet', 'sheets', 'OTH', ['sht']),
        u('plate', 'plates', 'OTH'),
        u('panel', 'panels', 'OTH'),
        u('slab', 'slabs', 'OTH'),
        u('block', 'blocks', 'OTH'),
        u('brick', 'bricks', 'NOS'),
        u('tray', 'trays', 'OTH'),
        u('crate', 'crates', 'OTH'),
        u('basket', 'baskets', 'OTH'),
        u('pouch', 'pouches', 'OTH'),
        u('sachet', 'sachets', 'OTH'),
        u('kit', 'kits', 'SET'),
        u('assortment', 'assortments', 'OTH'),
        u('lot', 'lots', 'OTH', ['batch']),
    ] },
    { group: 'Weight', units: [
        u('kg', 'kg', 'KGS', ['kilogram', 'kilograms', 'kgs', 'kilo']),
        u('gram', 'grams', 'GMS', ['g', 'gm', 'gms', 'gr']),
        u('milligram', 'milligrams', 'OTH', ['mg']),
        u('microgram', 'micrograms', 'OTH', ['mcg', 'µg']),
        u('tonne', 'tonnes', 'TON', ['metric ton', 'metric tonne', 't', 'ton']),
        u('MT', 'MT', 'MTS', ['metric ton', 'mts', 'mt']),
        u('quintal', 'quintals', 'QTL', ['qtl', '100 kg']),
        u('pound', 'pounds', 'OTH', ['lb', 'lbs']),
        u('ounce', 'ounces', 'OTH', ['oz']),
        u('troy ounce', 'troy ounces', 'OTH', ['ozt', 'oz t']),
        u('short ton', 'short tons', 'TON', ['us ton', '2000 lb']),
        u('long ton', 'long tons', 'TON', ['imperial ton', 'uk ton', '2240 lb']),
        u('hundredweight', 'hundredweight', 'OTH', ['cwt']),
        u('stone', 'stone', 'OTH', ['st']),
        u('carat', 'carats', 'OTH', ['ct', 'karat']),
        u('grain', 'grains', 'OTH', ['gr']),
        u('tola', 'tolas', 'OTH', ['11.66 g']),
        u('maund', 'maunds', 'OTH', ['mann', 'man']),
        u('seer', 'seers', 'OTH', ['ser']),
        u('candy', 'candies', 'OTH', ['khandi', '356 kg cotton']),
        u('picul', 'piculs', 'OTH'),
        u('catty', 'catties', 'OTH', ['jin']),
    ] },
    { group: 'Length', units: [
        u('meter', 'meters', 'MTR', ['m', 'metre', 'metres', 'mtr', 'mtrs']),
        u('running meter', 'running meters', 'MTR', ['rmt', 'rm', 'running metre', 'linear meter']),
        u('millimeter', 'millimeters', 'OTH', ['mm', 'millimetre']),
        u('centimeter', 'centimeters', 'CMS', ['cm', 'cms', 'centimetre']),
        u('kilometer', 'kilometers', 'KME', ['km', 'kilometre']),
        u('micron', 'microns', 'OTH', ['µm', 'micrometer']),
        u('inch', 'inches', 'OTH', ['in', '"']),
        u('foot', 'feet', 'OTH', ['ft', "'", 'feet']),
        u('running foot', 'running feet', 'OTH', ['rft', 'running ft', 'linear foot', 'lft']),
        u('yard', 'yards', 'YDS', ['yd', 'yds']),
        u('mile', 'miles', 'OTH', ['mi']),
        u('nautical mile', 'nautical miles', 'OTH', ['nmi']),
        u('gauge', 'gauge', 'OTH', ['swg', 'awg']),
    ] },
    { group: 'Area', units: [
        u('sq meter', 'sq meters', 'SQM', ['sqm', 'm2', 'm²', 'square meter', 'square metre', 'sq m', 'sq mtr']),
        u('sq ft', 'sq ft', 'SQF', ['sqft', 'ft2', 'ft²', 'square foot', 'square feet', 'sft']),
        u('sq inch', 'sq inches', 'OTH', ['sq in', 'in2', 'square inch']),
        u('sq yard', 'sq yards', 'SQY', ['sq yd', 'sqyd', 'yd2', 'square yard', 'gaj']),
        u('sq cm', 'sq cm', 'OTH', ['cm2', 'square centimeter']),
        u('sq mm', 'sq mm', 'OTH', ['mm2', 'square millimeter']),
        u('sq km', 'sq km', 'OTH', ['km2', 'square kilometer']),
        u('acre', 'acres', 'OTH', ['ac']),
        u('hectare', 'hectares', 'OTH', ['ha']),
        u('are', 'ares', 'OTH'),
        u('bigha', 'bighas', 'OTH'),
        u('guntha', 'gunthas', 'OTH', ['gunta']),
        u('cent', 'cents', 'OTH', ['decimal']),
        u('kanal', 'kanals', 'OTH'),
        u('marla', 'marlas', 'OTH'),
        u('biswa', 'biswas', 'OTH'),
        u('ground', 'grounds', 'OTH', ['2400 sq ft']),
    ] },
    { group: 'Volume & liquids', units: [
        u('litre', 'litres', 'LTR', ['l', 'liter', 'liters', 'ltr', 'ltrs']),
        u('ml', 'ml', 'MLT', ['millilitre', 'milliliter', 'mltr', 'cc']),
        u('kilolitre', 'kilolitres', 'KLR', ['kl', 'kiloliter', '1000 litres']),
        u('cubic meter', 'cubic meters', 'CBM', ['cbm', 'm3', 'm³', 'cu m', 'cubic metre']),
        u('cubic cm', 'cubic cm', 'CCM', ['ccm', 'cm3', 'cu cm', 'cubic centimeter']),
        u('cubic ft', 'cubic ft', 'OTH', ['cft', 'ft3', 'cu ft', 'cubic foot', 'cubic feet']),
        u('cubic inch', 'cubic inches', 'OTH', ['cu in', 'in3']),
        u('cubic yard', 'cubic yards', 'OTH', ['cu yd', 'yd3']),
        u('brass', 'brass', 'OTH', ['100 cft', 'sand brass']),
        u('gallon', 'gallons', 'OTH', ['gal', 'us gallon']),
        u('imperial gallon', 'imperial gallons', 'OTH', ['uk gallon', 'imp gal']),
        u('quart', 'quarts', 'OTH', ['qt']),
        u('pint', 'pints', 'OTH', ['pt']),
        u('fluid ounce', 'fluid ounces', 'OTH', ['fl oz']),
        u('barrel', 'barrels', 'OTH', ['bbl', 'oil barrel']),
        u('hectolitre', 'hectolitres', 'OTH', ['hl']),
        u('bushel', 'bushels', 'OTH', ['bu']),
        u('board foot', 'board feet', 'OTH', ['bf', 'fbm', 'timber']),
        u('cord', 'cords', 'OTH', ['firewood']),
    ] },
    { group: 'Shipping & logistics', units: [
        u('container', 'containers', 'OTH', ['fcl', 'cntr']),
        u('20ft container', '20ft containers', 'OTH', ['20 ft', "20'", 'teu', '20gp', '20 fcl']),
        u('40ft container', '40ft containers', 'OTH', ['40 ft', "40'", 'feu', '40gp', '40hc', '40 fcl']),
        u('TEU', 'TEU', 'OTH', ['twenty foot equivalent']),
        u('pallet', 'pallets', 'OTH', ['plt', 'skid']),
        u('truckload', 'truckloads', 'OTH', ['truck', 'ftl', 'lorry load']),
        u('wagon', 'wagons', 'OTH', ['rake', 'railcar']),
        u('shipload', 'shiploads', 'OTH', ['vessel', 'bulk vessel']),
        u('trip', 'trips', 'OTH', ['load']),
    ] },
    { group: 'Textiles, yarn & paper', units: [
        u('thaan', 'thaans', 'THD', ['than', 'bolt']),
        u('bolt', 'bolts', 'OTH'),
        u('cone', 'cones', 'OTH', ['yarn cone']),
        u('hank', 'hanks', 'OTH'),
        u('spool', 'spools', 'OTH', ['bobbin']),
        u('ream', 'reams', 'OTH', ['500 sheets']),
        u('quire', 'quires', 'OTH'),
        u('gsm', 'gsm', 'OTH', ['grams per square meter']),
        u('denier', 'denier', 'OTH', ['den']),
        u('tex', 'tex', 'OTH'),
    ] },
    { group: 'Pharma & retail packs', units: [
        u('tablet', 'tablets', 'NOS', ['tab', 'tabs']),
        u('capsule', 'capsules', 'NOS', ['cap', 'caps']),
        u('strip', 'strips', 'OTH'),
        u('blister', 'blisters', 'OTH', ['blister pack']),
        u('vial', 'vials', 'NOS'),
        u('ampoule', 'ampoules', 'NOS', ['ampule', 'amp']),
        u('dose', 'doses', 'OTH'),
        u('IU', 'IU', 'OTH', ['international unit']),
        u('prefilled syringe', 'prefilled syringes', 'NOS', ['pfs']),
    ] },
    { group: 'Energy & power', units: [
        u('kWh', 'kWh', 'OTH', ['kilowatt hour', 'unit of electricity']),
        u('MWh', 'MWh', 'OTH', ['megawatt hour']),
        u('kW', 'kW', 'OTH', ['kilowatt']),
        u('MW', 'MW', 'OTH', ['megawatt']),
        u('kVA', 'kVA', 'OTH', ['kilovolt ampere']),
        u('HP', 'HP', 'OTH', ['horsepower']),
        u('ton of refrigeration', 'tons of refrigeration', 'OTH', ['tr', 'ac ton']),
        u('BTU', 'BTU', 'OTH', ['british thermal unit']),
        u('MMBTU', 'MMBTU', 'OTH', ['million btu']),
        u('therm', 'therms', 'OTH'),
        u('SCM', 'SCM', 'OTH', ['standard cubic meter', 'gas']),
        u('ampere hour', 'ampere hours', 'OTH', ['ah', 'amp hour']),
        u('watt peak', 'watt peak', 'OTH', ['wp', 'solar watt']),
    ] },
    { group: 'Time & services', units: [
        u('hour', 'hours', 'OTH', ['hr', 'hrs']),
        u('man-hour', 'man-hours', 'OTH', ['man hour', 'labour hour']),
        u('day', 'days', 'OTH'),
        u('man-day', 'man-days', 'OTH', ['man day']),
        u('shift', 'shifts', 'OTH'),
        u('week', 'weeks', 'OTH', ['wk']),
        u('month', 'months', 'OTH', ['mo', 'monthly']),
        u('quarter', 'quarters', 'OTH', ['qtr']),
        u('year', 'years', 'OTH', ['yr', 'annual', 'annum']),
        u('job', 'jobs', 'OTH', ['job work', 'project']),
        u('visit', 'visits', 'OTH'),
        u('session', 'sessions', 'OTH'),
        u('user', 'users', 'OTH', ['seat', 'licence', 'license']),
        u('page', 'pages', 'OTH'),
        u('word', 'words', 'OTH'),
    ] },
    { group: 'Digital & data', units: [
        u('MB', 'MB', 'OTH', ['megabyte']),
        u('GB', 'GB', 'OTH', ['gigabyte']),
        u('TB', 'TB', 'OTH', ['terabyte']),
        u('API call', 'API calls', 'OTH', ['request']),
    ] },
    { group: 'Other', units: [
        u('percent', 'percent', 'OTH', ['%']),
        u('ppm', 'ppm', 'OTH', ['parts per million']),
        u('degree', 'degrees', 'OTH', ['°']),
    ] },
];

export const UNITS = UNIT_GROUPS.flatMap(({ group, units }) => units.map((x) => ({ ...x, group })));

const norm = (s) => String(s || '').trim().toLowerCase();
const BY_NAME = new Map();
// Exact names first, then plurals, then aliases, so "mt" finds "MT", not "tonne".
for (const pass of ['one', 'many', 'aka']) {
    for (const x of UNITS) {
        for (const key of pass === 'aka' ? x.aka : [x[pass]]) if (!BY_NAME.has(norm(key))) BY_NAME.set(norm(key), x);
    }
}
/** The unit a stored value, plural, abbreviation or UQC code means — or null. */
export function findUnit(text) {
    const key = norm(text).replace(/^\/\s*/, '');
    if (!key) return null;
    return BY_NAME.get(key) || UNITS.find((x) => x.uqc && x.uqc.toLowerCase() === key && x.uqc !== 'OTH') || null;
}

/** Search for the picker: ranks name starts > name contains > alias / UQC / group. */
export function searchUnits(query, limit = 80) {
    const q = norm(query);
    if (!q) return UNITS;
    const scored = [];
    for (const x of UNITS) {
        const one = norm(x.one);
        const many = norm(x.many);
        let score = 0;
        if (one === q || many === q) score = 100;
        else if (one.startsWith(q) || many.startsWith(q)) score = 80;
        else if (x.aka.some((a) => norm(a) === q)) score = 70;
        else if (one.includes(q) || many.includes(q)) score = 60;
        else if (x.aka.some((a) => norm(a).startsWith(q))) score = 50;
        else if (x.aka.some((a) => norm(a).includes(q))) score = 30;
        else if (x.uqc.toLowerCase() === q) score = 25;
        else if (norm(x.group).includes(q)) score = 10;
        if (score) scored.push([score, x]);
    }
    return scored.sort((a, b) => b[0] - a[0]).slice(0, limit).map(([, x]) => x);
}
